import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import FormatListNumberedRounded from '@mui/icons-material/FormatListNumberedRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import MarkEmailUnreadRounded from '@mui/icons-material/MarkEmailUnreadRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import PlaylistAddCheckRounded from '@mui/icons-material/PlaylistAddCheckRounded';
import RuleFolderRounded from '@mui/icons-material/RuleFolderRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import NoteAddRounded from '@mui/icons-material/NoteAddRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import PhoneMissedRounded from '@mui/icons-material/PhoneMissedRounded';
import CallRounded from '@mui/icons-material/CallRounded';
import { fetchTenantAgents } from '../../services/tenantAgentsService';

/**
 * The left column while you are inside an agent.
 *
 * An agent is a small application — what feeds it, how it decides, how it
 * speaks, what it is allowed to do, and then what it actually did — so it
 * takes over the sidebar the way Copilot does for a thread, rather than
 * hiding those sections behind tabs. The way back out is the first thing in
 * the column, so nobody is ever stuck inside.
 */

export interface AgentSection {
  id: string;
  label: string;
  icon: React.ElementType;
}

export interface AgentSectionGroup {
  heading: string;
  items: AgentSection[];
}

export const ROCCO_SECTIONS: AgentSectionGroup[] = [
  {
    // One flat list rather than headings: six rows do not need grouping,
    // and the groups made the column look fuller than it is. The last three
    // are the live ones: a lead moves down them as the work is done.
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'queue', label: 'Live queue', icon: FormatListNumberedRounded },
      { id: 'callbacks', label: 'Scheduled callbacks', icon: EventAvailableRounded },
      { id: 'conversations', label: 'Conversations', icon: ForumRounded },
    ],
  },
];

// Same order for every agent: the overview, how it is set up, what it is
// joined to, then its live lists in the order work moves down them.
export const POLICY_SERVICING_SECTIONS: AgentSectionGroup[] = [
  {
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'live', label: 'Live requests', icon: MarkEmailUnreadRounded },
      { id: 'completed', label: 'Completed requests', icon: TaskAltRounded },
      { id: 'accounts', label: 'Accounts', icon: ApartmentRounded },
    ],
  },
];

export const POLICY_CHECKING_SECTIONS: AgentSectionGroup[] = [
  {
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'new', label: 'New check', icon: RuleFolderRounded },
      { id: 'checks', label: 'Checks', icon: PlaylistAddCheckRounded },
    ],
  },
];

export const CARRIER_CORRESPONDENCE_SECTIONS: AgentSectionGroup[] = [
  {
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'cases', label: 'Cases', icon: HourglassBottomRounded },
      { id: 'conversations', label: 'Conversations', icon: ForumRounded },
    ],
  },
];

export const CARRIER_QUOTING_SECTIONS: AgentSectionGroup[] = [
  {
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'new', label: 'New quote', icon: NoteAddRounded },
      { id: 'quotes', label: 'Quotes', icon: RequestQuoteRounded },
    ],
  },
];

export const MISSED_CALL_SECTIONS: AgentSectionGroup[] = [
  {
    heading: '',
    items: [
      { id: 'spotlight', label: 'Spotlight', icon: InsightsRounded },
      { id: 'configuration', label: 'Agent Configuration', icon: TuneRounded },
      { id: 'integrations', label: 'Integrations', icon: CloudSyncRounded },
      { id: 'cases', label: 'Missed calls', icon: PhoneMissedRounded },
      { id: 'calls', label: 'Calls', icon: CallRounded },
    ],
  },
];

/** Each agent kind's sections, by the kind in the address. */
export const AGENT_SECTIONS: Record<string, AgentSectionGroup[]> = {
  rocco: ROCCO_SECTIONS,
  missed_call: MISSED_CALL_SECTIONS,
  policy_servicing: POLICY_SERVICING_SECTIONS,
  policy_checking: POLICY_CHECKING_SECTIONS,
  carrier_correspondence: CARRIER_CORRESPONDENCE_SECTIONS,
  carrier_quoting: CARRIER_QUOTING_SECTIONS,
};

/** Each kind's usual name, for the path bar at the top of the page. */
export const AGENT_NAMES: Record<string, string> = {
  rocco: 'ROCCO',
  missed_call: 'Missed Call Follow-up',
  policy_servicing: 'Policy Servicing',
  policy_checking: 'Policy Checking',
  carrier_correspondence: 'Carrier Correspondence',
  carrier_quoting: 'Carrier Quoting',
};

/** The name and area shown before the workspace's own name for the agent arrives. */
const FALLBACK_HEADER: Record<string, { name: string; category: string }> = {
  rocco: { name: 'ROCCO', category: 'Sales' },
  missed_call: { name: 'Missed Call Follow-up', category: 'Sales' },
  policy_servicing: { name: 'Policy Servicing', category: 'Service' },
  policy_checking: { name: 'Policy Checking', category: 'Compliance' },
  carrier_correspondence: { name: 'Carrier Correspondence', category: 'Service' },
  carrier_quoting: { name: 'Carrier Quoting', category: 'Sales' },
};

export default function AgentSidebar({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { agentKind, playbookId } = useParams();
  const agentId = agentKind || playbookId || 'rocco';
  const active = location.pathname.split('/')[3] || 'spotlight';
  const sections = AGENT_SECTIONS[agentId] ?? ROCCO_SECTIONS;
  const [header, setHeader] = React.useState(FALLBACK_HEADER[agentId] ?? { name: agentId, category: 'Agent' });

  // The workspace's own name for it, so a rename on the Agents page shows here too.
  React.useEffect(() => {
    let live = true;
    setHeader(FALLBACK_HEADER[agentId] ?? { name: agentId, category: 'Agent' });
    fetchTenantAgents()
      .then((agents) => {
        const agent = agents.find((a) => a.kind === agentId);
        if (live && agent) {
          setHeader({ name: agent.name, category: agent.category || FALLBACK_HEADER[agentId]?.category || 'Agent' });
        }
      })
      .catch(() => undefined);
    return () => { live = false; };
  }, [agentId]);

  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
        <ButtonBase
          onClick={() => navigate('/agents')}
          aria-label="Back to Agents"
          sx={(t) => ({
            p: 1,
            borderRadius: `${t.ds.radius.sm}px`,
            color: t.tokens.sidebar.itemFg,
            '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
          })}
        >
          <ArrowBackRounded sx={{ fontSize: 18 }} />
        </ButtonBase>
      </Box>
    );
  }

  // Fills the column the app shell gives it, above Logout and the support card.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 1 }}>
      <ButtonBase
        onClick={() => navigate('/agents')}
        sx={(t) => ({
          width: '100%',
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.25,
          py: 0.875,
          borderRadius: `${t.ds.radius.sm}px`,
          color: t.tokens.text.secondary,
          '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
        })}
      >
        <ArrowBackRounded sx={{ fontSize: 16 }} />
        <Typography variant="body2">Back to Agents</Typography>
      </ButtonBase>

      <Box
        sx={(t) => ({
          mt: 1,
          mx: 0.5,
          px: 1.25,
          py: 0.75,
          borderRadius: `${t.ds.radius.md}px`,
          border: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={(t) => ({
            width: 8, height: 8, borderRadius: '50%', bgcolor: t.tokens.semantic.success,
          })} />
          <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {header.name}
          </Typography>
        </Stack>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          {header.category} · running
        </Typography>
      </Box>

      {sections.map((group, gi) => (
        <Box key={group.heading || `g${gi}`} sx={{ mt: 1.25 }}>
          {group.heading ? (
            <Typography
              variant="overline"
              sx={(t) => ({ px: 1.75, color: t.tokens.sidebar.sectionLabel, display: 'block', mb: 0.5 })}
            >
              {group.heading}
            </Typography>
          ) : null}
          <Stack spacing={0.25}>
            {group.items.map((item) => {
              const Icon = item.icon;
              const selected = active === item.id;
              return (
                <ButtonBase
                  key={item.id}
                  onClick={() => navigate(`/agents/${agentId}/${item.id}`)}
                  sx={(t) => ({
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 1.25,
                    px: 1.25,
                    py: 0.625,
                    borderRadius: `${t.ds.radius.sm}px`,
                    color: selected ? t.tokens.brand.primary : t.tokens.sidebar.itemFg,
                    bgcolor: selected ? t.tokens.sidebar.itemActiveBg : 'transparent',
                    fontWeight: selected ? 600 : 400,
                    '&:hover': { bgcolor: selected ? t.tokens.sidebar.itemActiveBg : t.tokens.sidebar.itemHoverBg },
                  })}
                >
                  <Icon sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>{item.label}</Typography>
                </ButtonBase>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
